import { useGroupChannelContext } from "@sendbird/uikit-react/GroupChannel/context";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { useQueryClient } from "@tanstack/react-query";
import { useVoiceRecorder } from "@somethings-ui/web";
import type { ComposerVariant } from "@somethings-ui/web";
import { useCallback, useMemo, useState } from "react";
import type { FileMessage, UserMessage } from "@sendbird/chat/message";
import { saveSentMessage } from "@/lib/api/messages";
import { useAudioTranscoder } from "@/lib/hooks/use-audio-transcoder";
import { useMessageInputStore } from "@/lib/hooks/conversations/use-message-input";
import type { AttentionQuote } from "./attention-message";
import type {
  AttentionComposerEditor,
  AttentionComposerGif,
  AttentionComposerImage,
  AttentionComposerMode,
  AttentionComposerModeControls,
  AttentionComposerQuote,
  AttentionComposerVoice,
  AttentionSendStatus,
  AttentionVoiceView,
} from "./attention-composer-model";
import {
  createAttentionFileMessageSender,
  createAttentionUserMessageSender,
} from "./attention-send-adapter";
import {
  buildAttentionSendPlan,
  type AttentionSendPlan,
} from "./attention-send-plan";
import { useAttentionMediaStaging } from "./use-attention-media-staging";
import type { UseAttentionAnalyticsResult } from "./use-attention-analytics";

export interface UseAttentionComposerArgs {
  channelUrl: string;
  quote: AttentionQuote | null;
  onQuoteChange: (quote: AttentionQuote | null) => void;
  bannerActive?: boolean;
  analytics: UseAttentionAnalyticsResult;
}

export interface AttentionComposerViewModel {
  editor: AttentionComposerEditor;
  quote: AttentionComposerQuote;
  mode: AttentionComposerModeControls;
  image: AttentionComposerImage;
  gif: AttentionComposerGif;
  voice: AttentionComposerVoice;
  insertSuggestion: (text: string) => void;
}

const SEND_ERROR_MESSAGE = "Message failed to send. Try again.";

export function useAttentionComposer({
  channelUrl,
  quote,
  onQuoteChange,
  bannerActive = false,
  analytics,
}: UseAttentionComposerArgs): AttentionComposerViewModel {
  const store = useSendbirdStateContext();
  const currentUserId = store.config.userId;
  const { currentChannel } = useGroupChannelContext();
  const queryClient = useQueryClient();

  const { messageInputs, setMessageInput } = useMessageInputStore();
  const draft = messageInputs[channelUrl] ?? "";

  const [status, setStatus] = useState<AttentionSendStatus>("idle");
  const [mode, setMode] = useState<AttentionComposerMode>("idle");

  const media = useAttentionMediaStaging();
  const { clearImage, clearGif } = media;
  const voice = useVoiceRecorder();
  const { transcodeToM4a } = useAudioTranscoder();

  const setDraft = useCallback(
    (value: string) => setMessageInput(channelUrl, value),
    [channelUrl, setMessageInput],
  );

  const clearQuote = useCallback(() => onQuoteChange(null), [onQuoteChange]);

  const toUserId = useMemo(
    () =>
      currentChannel?.members.find(
        (member) => member.userId !== currentUserId,
      )?.userId ?? null,
    [currentChannel, currentUserId],
  );

  const insertSuggestion = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (trimmed.length === 0) return;
      const current = draft;
      const next =
        current.trim().length === 0
          ? trimmed
          : `${current}${current.endsWith(" ") ? "" : " "}${trimmed}`;
      setDraft(next);
    },
    [draft, setDraft],
  );

  const trackModeOpen = useCallback(
    (target: Exclude<AttentionComposerMode, "idle">) => {
      analytics.trackToolbarAction(target);
      if (target === "suggestions") analytics.trackSuggestionOpen();
    },
    [analytics],
  );

  const changeMode = useCallback(
    (next: AttentionComposerMode) => {
      if (mode === "image" && next !== "image") clearImage();
      if (mode === "gif" && next !== "gif") clearGif();
      if (mode === "voice" && next !== "voice") voice.cancel();
      if (mode === "suggestions" && next !== "suggestions") {
        analytics.trackSuggestionDismiss(null);
      }
      setMode(next);
    },
    [analytics, mode, clearImage, clearGif, voice],
  );

  const toggleMode = useCallback(
    (target: AttentionComposerMode) => {
      const willOpen = mode !== target;
      if (willOpen && target !== "idle") trackModeOpen(target);
      changeMode(willOpen ? target : "idle");
    },
    [changeMode, mode, trackModeOpen],
  );

  const closeMode = useCallback(() => changeMode("idle"), [changeMode]);

  const startVoice = useCallback(() => {
    if (mode !== "voice") {
      trackModeOpen("voice");
      setMode("voice");
    }
    void voice.start();
  }, [mode, voice, trackModeOpen]);

  const stopVoice = useCallback(() => {
    void voice.stop();
  }, [voice]);

  const pauseVoice = useCallback(() => {
    voice.pause();
  }, [voice]);

  const resumeVoice = useCallback(() => {
    voice.resume();
  }, [voice]);

  const discardVoice = useCallback(() => {
    voice.discard();
    setMode("idle");
  }, [voice]);

  const sendUserMessage = useMemo(
    () => createAttentionUserMessageSender(store),
    [store],
  );
  const sendFileMessage = useMemo(
    () => createAttentionFileMessageSender(store),
    [store],
  );

  const persist = useCallback(
    async (message: UserMessage | FileMessage, file: File | null) => {
      if (!toUserId) return;
      const didPersist = await saveSentMessage(message, file, toUserId);
      if (didPersist) {
        void queryClient.invalidateQueries({ queryKey: ["mentorship"] });
      }
    },
    [toUserId, queryClient],
  );

  const canSend = useMemo(() => {
    switch (mode) {
      case "voice":
        return voice.status === "recorded" && voice.recording !== null;
      case "image":
        return media.imageFile !== null;
      case "gif":
        return media.selectedGif !== null;
      case "idle":
      case "emoji":
      case "suggestions":
        return draft.trim().length > 0;
      default: {
        const exhaustive: never = mode;
        return exhaustive;
      }
    }
  }, [mode, voice.status, voice.recording, media.imageFile, media.selectedGif, draft]);

  // Optimistic reset synchronously; Sendbird owns pending/failed/resend UI.
  const execute = useCallback(
    (plan: AttentionSendPlan | null) => {
      if (plan === null) return;
      setStatus("idle");
      plan.reset();
      setMode("idle");
      onQuoteChange(null);
      plan
        .run()
        .then(() => plan.onSuccess())
        .catch(() => {
          setStatus("error");
        });
    },
    [onQuoteChange],
  );

  const send = useCallback(() => {
    const plan = buildAttentionSendPlan({
      mode,
      draft,
      quote,
      channel: currentChannel,
      currentUserId,
      imageFile: media.imageFile,
      selectedGif: media.selectedGif,
      voice: {
        status: voice.status,
        recording: voice.recording,
        elapsedMs: voice.elapsedMs,
        discard: voice.discard,
      },
      clearImage: media.clearImage,
      clearGif: media.clearGif,
      setDraft,
      sendUserMessage,
      sendFileMessage,
      persist,
      transcodeToM4a,
      trackMessageSend: analytics.trackMessageSend,
      trackVoiceSend: analytics.trackVoiceSend,
    });
    if (plan === null && !currentChannel) {
      setStatus("error");
      return;
    }
    execute(plan);
  }, [
    mode,
    draft,
    quote,
    currentChannel,
    currentUserId,
    media.imageFile,
    media.selectedGif,
    media.clearImage,
    media.clearGif,
    voice.status,
    voice.recording,
    voice.elapsedMs,
    voice.discard,
    setDraft,
    sendUserMessage,
    sendFileMessage,
    persist,
    transcodeToM4a,
    analytics.trackMessageSend,
    analytics.trackVoiceSend,
    execute,
  ]);

  const hasPanel =
    mode === "image" ||
    (mode === "gif" && media.selectedGif !== null) ||
    mode === "suggestions" ||
    quote !== null;
  const variant: ComposerVariant = hasPanel
    ? "panel"
    : bannerActive
      ? "banner"
      : "default";

  const voiceView = useMemo<AttentionVoiceView>(
    () => ({
      status: voice.status,
      isRecording: voice.isRecording,
      isPaused: voice.isPaused,
      isSupported: voice.isSupported,
      amplitudes: voice.amplitudes,
      time: voice.time,
      recording:
        voice.recording !== null
          ? { url: voice.recording.url, amplitudes: voice.recording.amplitudes }
          : null,
    }),
    [
      voice.status,
      voice.isRecording,
      voice.isPaused,
      voice.isSupported,
      voice.amplitudes,
      voice.time,
      voice.recording,
    ],
  );

  const editor = useMemo<AttentionComposerEditor>(
    () => ({
      draft,
      onDraftChange: setDraft,
      canSend,
      status,
      errorMessage: status === "error" ? SEND_ERROR_MESSAGE : media.mediaError,
      onSend: send,
    }),
    [draft, setDraft, canSend, status, media.mediaError, send],
  );

  const quoteSlice = useMemo<AttentionComposerQuote>(
    () => ({ value: quote, onClear: clearQuote }),
    [quote, clearQuote],
  );

  const modeControls = useMemo<AttentionComposerModeControls>(
    () => ({ current: mode, variant, onToggle: toggleMode, onClose: closeMode }),
    [mode, variant, toggleMode, closeMode],
  );

  const image = useMemo<AttentionComposerImage>(
    () => ({
      preview: media.imagePreview,
      kind: media.imageKind,
      converting: media.converting,
      onSelectFile: media.selectImageFile,
      onClear: media.clearImage,
    }),
    [
      media.imagePreview,
      media.imageKind,
      media.converting,
      media.selectImageFile,
      media.clearImage,
    ],
  );

  const gif = useMemo<AttentionComposerGif>(
    () => ({
      selected: media.selectedGif,
      onSelect: media.selectGif,
      onClear: media.clearGif,
    }),
    [media.selectedGif, media.selectGif, media.clearGif],
  );

  const voiceSlice = useMemo<AttentionComposerVoice>(
    () => ({
      view: voiceView,
      onStart: startVoice,
      onStop: stopVoice,
      onPause: pauseVoice,
      onResume: resumeVoice,
      onDiscard: discardVoice,
    }),
    [voiceView, startVoice, stopVoice, pauseVoice, resumeVoice, discardVoice],
  );

  return {
    editor,
    quote: quoteSlice,
    mode: modeControls,
    image,
    gif,
    voice: voiceSlice,
    insertSuggestion,
  };
}
